import { useEffect, useState } from 'react'
import type { Panel, PanelDictionaryEntry } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { PageHeader, DataTable, Pagination, ColumnSettingsButton, btnPrimary, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api } from '../../services/api'
import { PanelDictionaryEditModal } from './PanelDictionaryEditModal'
import { useTableColumns } from '../../hooks/use-table-columns'

export function PanelDictionaryListPage() {
  const { data, total, page, loading, setPage, refresh } = useApiList<PanelDictionaryEntry>({
    path: '/panel-dictionary',
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('panel-dictionary')
  const [panels, setPanels] = useState<readonly Panel[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly Panel[]>('/panels?isActive=true&limit=100')
      .then((res) => {
        if (cancelled) return
        if (res.data) setPanels(res.data)
      })
      .catch(() => {
        // non-fatal
      })
    return () => {
      cancelled = true
    }
  }, [])

  function openEditModal(entry: PanelDictionaryEntry) {
    setSelectedId(entry.id)
    setIsNew(false)
    setModalOpen(true)
  }

  function openNewModal() {
    setSelectedId(null)
    setIsNew(true)
    setModalOpen(true)
  }

  const panelById = new Map(panels.map((p) => [p.id, p]))

  const columns: readonly Column<PanelDictionaryEntry>[] = [
    {
      key: 'scope',
      header: 'Scope',
      sortable: true,
      render: (e) => {
        if (!e.panelId) return <strong>Organization default</strong>
        const p = panelById.get(e.panelId)
        return p ? `${p.code} - ${p.name}` : e.panelId.slice(0, 8)
      },
    },
    { key: 'interpretations', header: 'Interpretations', render: (e) => `${e.interpretationTemplates.length}` },
    { key: 'rejections', header: 'Rejection Reasons', render: (e) => `${e.rejectionReasons.length}` },
    {
      key: 'updated',
      header: 'Updated',
      sortable: true,
      render: (e) => new Date(e.updatedAt).toLocaleDateString(),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Panel Dictionary"
        subtitle={`${total} entries`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={openNewModal} style={btnPrimary}>
              Add Entry
            </button>
          </>
        }
      />
      <p style={{ color: colors.textMuted, fontSize: '0.875rem', margin: '0 0 1rem' }}>
        Defines result interpretation templates and controlled rejection reasons. An entry without a panel applies as
        the organization default; per-panel entries override the default.
      </p>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={openEditModal}
            emptyMessage="No dictionary entries yet. Add an organization default to get started."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <PanelDictionaryEditModal
        entryId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => refresh()}
        onDeleted={() => refresh()}
      />
    </div>
  )
}
